/**
 * SZCA 会话仅 localStorage，勿在此文件静态 import lib/cert（node-forge），
 * 否则 store / OpenProcess 首屏会打进 forge。
 * 密钥恢复见 restoreKeyPairToPState（动态 import）。
 */
import { location } from 'cdui-js';

export const SZCA_PENDING_KEY = 'open_progress_szca_pending';
/** 风险披露拿齐证书后缓存，供手写签名页 signFieldFileAcquire 使用 */
export const SZCA_CERT_CACHE_KEY = 'open_progress_szca_cert';
/** CA 回跳 URL 的 certificateSerialNumber（与 findCertDown 的 certSn 分开存） */
export const SZCA_SERIAL_KEY = 'open_progress_szca_serial';

export const SZCA_SERIAL_QUERY_KEY = 'certificateSerialNumber';

export type SzcaPendingState = {
  busId: string;
  certDn: string;
  bizNo?: string;
  resId?: string;
  privateKeyPem: string;
  publicKeyPem: string;
  context?: string;
};

/** 已完成 CA 的证书信息（含可选密钥，供签名 PKCS1） */
export type SzcaCertCache = {
  busId: string;
  certDn: string;
  certSn: string;
  privateKeyPem?: string;
  publicKeyPem?: string;
};

export type SzcaSerialCache = {
  busId: string;
  certificateSerialNumber: string;
};

export function saveSzcaPendingState(state: SzcaPendingState) {
  try {
    localStorage.setItem(SZCA_PENDING_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

export function loadSzcaPendingState(): SzcaPendingState | null {
  try {
    const raw = localStorage.getItem(SZCA_PENDING_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SzcaPendingState;
  } catch {
    return null;
  }
}

export function clearSzcaPendingState() {
  try {
    localStorage.removeItem(SZCA_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

export function saveSzcaCertCache(state: SzcaCertCache) {
  try {
    localStorage.setItem(SZCA_CERT_CACHE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

export function loadSzcaCertCache(busId?: string | number): SzcaCertCache | null {
  try {
    const raw = localStorage.getItem(SZCA_CERT_CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as SzcaCertCache;
    if (!data?.certDn || !data?.certSn) return null;
    if (busId != null && String(data.busId) !== String(busId)) return null;
    return data;
  } catch {
    return null;
  }
}

export function clearSzcaCertCache() {
  try {
    localStorage.removeItem(SZCA_CERT_CACHE_KEY);
  } catch {
    /* ignore */
  }
  clearSzcaSerialCache();
}

export function saveSzcaSerialCache(state: SzcaSerialCache) {
  try {
    localStorage.setItem(SZCA_SERIAL_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

export function loadSzcaSerialCache(busId?: string | number): SzcaSerialCache | null {
  try {
    const raw = localStorage.getItem(SZCA_SERIAL_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as SzcaSerialCache;
    if (!data?.certificateSerialNumber) return null;
    if (busId != null && String(data.busId) !== String(busId)) return null;
    return data;
  } catch {
    return null;
  }
}

export function clearSzcaSerialCache() {
  try {
    localStorage.removeItem(SZCA_SERIAL_KEY);
  } catch {
    /* ignore */
  }
}

/** 从回跳 URL 读取 certificateSerialNumber 并单独落盘（不写入 certSn） */
export function captureCertificateSerialFromQuery(busId: string | number): string {
  try {
    let serial = String((location.query as Record<string, string | undefined>)[SZCA_SERIAL_QUERY_KEY] ?? '').trim();
    if (!serial && typeof window !== 'undefined') {
      serial = String(new URL(window.location.href).searchParams.get(SZCA_SERIAL_QUERY_KEY) || '').trim();
    }
    if (!serial) return loadSzcaSerialCache(busId)?.certificateSerialNumber || '';
    saveSzcaSerialCache({ busId: String(busId), certificateSerialNumber: serial });
    return serial;
  } catch {
    return loadSzcaSerialCache(busId)?.certificateSerialNumber || '';
  }
}

/** 本地是否已有 certSn，或 URL/缓存中有 certificateSerialNumber */
export function hasCertSnOrSerial(busId: string | number, certSn?: string): boolean {
  if (certSn) return true;
  if (loadSzcaCertCache(busId)?.certSn) return true;
  if (loadSzcaSerialCache(busId)?.certificateSerialNumber) return true;
  try {
    const q = location.query as Record<string, string | undefined>;
    if (String(q[SZCA_SERIAL_QUERY_KEY] ?? '').trim()) return true;
    if (typeof window !== 'undefined') {
      const fromUrl = new URL(window.location.href).searchParams.get(SZCA_SERIAL_QUERY_KEY);
      if (String(fromUrl || '').trim()) return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * 接口返回 certDn/certSn 后双写：内存 pState + localStorage。
 * 后续签名页 / 整页回跳依赖缓存，不可只留在内存。
 */
export function persistSzcaCertToStore(
  target: { id: string | number; certDn: string; certSn: string },
  certDn: string,
  certSn: string,
  pems?: { privateKeyPem?: string; publicKeyPem?: string },
) {
  if (!certDn || !certSn) return;
  target.certDn = certDn;
  target.certSn = certSn;
  saveSzcaCertCache({
    busId: String(target.id),
    certDn,
    certSn,
    privateKeyPem: pems?.privateKeyPem,
    publicKeyPem: pems?.publicKeyPem,
  });
}

/**
 * 从 localStorage 恢复证书到 pState（整页回跳后内存为空时用）。
 * 返回 true 表示已有可用的 certDn + certSn。
 */
export function hydrateSzcaCertToPState(target: {
  id: string | number;
  certDn: string;
  certSn: string;
}): boolean {
  if (target.certDn && target.certSn) return true;
  const cached = loadSzcaCertCache(target.id);
  if (!cached) return false;
  if (!target.certDn) target.certDn = cached.certDn;
  if (!target.certSn) target.certSn = cached.certSn;
  return !!(target.certDn && target.certSn);
}

export async function restoreKeyPairToPState(
  pState: { keyPair: unknown; certDn: string },
  pending: Pick<SzcaPendingState, 'certDn' | 'privateKeyPem' | 'publicKeyPem'>,
) {
  const { importKeyPairFromPem } = await import('../../phases/open-cert');
  pState.certDn = pending.certDn;
  pState.keyPair = importKeyPairFromPem(pending.privateKeyPem, pending.publicKeyPem);
}

/** 是否存在外跳 SZCA 后待处理的会话（回跳后由 findCertDown 判定认证结果） */
export function hasSzcaPendingSession() {
  return !!loadSzcaPendingState();
}
